import React, { useState } from "react";
import { Formik } from "formik";
import { Form, InputGroup, Button, Container, Row, Col } from "react-bootstrap";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import {
  employersInputs,
  validationSchema,
} from "../formEntities/employersEntities";
import { AiOutlinePlus } from "react-icons/ai";
import formatService from "../../../services/formatService";

const EmployersForm = ({
  shake,
  handleNoEmployers,
  noEmployers,
  handleFormikSubmit,
  initialValues,
  employers,
  userData
}) => {
  const [address2, setAddress2] = useState(false);

  // Convert MM/DD/YYYY to YYYY-MM-DD for date input
  const toDateInputFormat = (dateStr) => {
    if (!dateStr || dateStr.length !== 10) return "";
    const [month, day, year] = dateStr.split("/");
    return `${year}-${month}-${day}`;
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for storage
  const toStorageFormat = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${month}/${day}/${year}`;
  };

  // Get today's date in YYYY-MM-DD format for max attribute
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const styles = {
    errorStyle: {
      border: "1px solid red",
      borderTopRightRadius: "0",
      borderBottomRightRadius: "0",
    },
    fieldErrMessage: {
      color: "red",
      paddingLeft: "5px",
    },
    errIcon: {
      fontSize: "20px",
      color: "red",
    },
    inputGroupTextStyle: {
      border: "1px solid red",
      background: "white",
    },
    errText: {
      color: "red",
    },
  };

  const handleAdd = (values) => {
    if (
      !values.employerName ||
      !values.employerPhone ||
      !values.startDate ||
      !values.employerAddress1 ||
      !values.employerCity ||
      !values.employerState ||
      !values.employerZip ||
      !values.supervisorName ||
      !values.jobTitle ||
      !values.startRate ||
      !values.jobDuties
    ) {
      shake();
    }
  };

  const handleValidations = (values) => {
    let errors = {};
    if (
      values.employerPhone &&
      !/^\(?(\d{3})\)?[- ]?(\d{3})[- ]?(\d{4})$/.test(values.employerPhone)
    ) {
      errors.employerPhone = "Invalid format.  Use (###) ###-####";
    }

    // Validate startDate is not in the future
    if (values.startDate && values.startDate !== "") {
      const startDate = new Date(values.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate > today) {
        errors.startDate = "Start date cannot be in the future";
      }
    }

    // Validate endDate is not in the future
    if (values.endDate && values.endDate !== "") {
      const endDate = new Date(values.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (endDate > today) {
        errors.endDate = "End date cannot be in the future";
      }
    }

    // Validate endDate is after startDate
    if (values.startDate && values.endDate) {
      const startDate = new Date(values.startDate);
      const endDate = new Date(values.endDate);
      if (endDate < startDate) {
        errors.endDate = "End date must be after start date";
      }
    }

    if (values.employerZip && !/^[0-9]+$/i.test(values.employerZip)) {
      errors.employerZip = "Must be all numeric values";
    }

    if (
      values.startRate &&
      !/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*\.[0-9]{2}$/i.test(values.startRate)
    ) {
      errors.startRate = "Invalid Format.  Use XX.XX";
    }

    if (
      values.endRate &&
      !/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*\.[0-9]{2}$/i.test(values.endRate)
    ) {
      errors.endRate = "Invalid Format.  Use XX.XX";
    }
    return errors;
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      validate={(values) => handleValidations(values)}
      onSubmit={(values, actions) => handleFormikSubmit(values, actions)}
    >
      {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
        <Container className="px-0">
          {/* {console.log("Values:", values, "\nErrors:", errors)} */}
          <Form noValidate onSubmit={handleSubmit}>
            {/* Skip Step Checkbox */}
            {employers && employers.length === 0 && (
              <section className="text-start">
                <p>
                  If you have never been employed, click the checkbox below.
                </p>
                <Form.Group
                  className="skip-this-step text-start"
                  controlId="form-basic-checkbox"
                >
                  <Form.Check
                    type="checkbox"
                    label="I have no employment information."
                    onChange={() => handleNoEmployers()}
                    checked={noEmployers ? true : false}
                    value={noEmployers}
                  />
                </Form.Group>
              </section>
            )}
            <hr />

            {/* Inputs  */}
            <section>
              <Container>
                <Row>
                  {employersInputs.map((input, index) => (
                    <Col
                      sm={12}
                      md={6}
                      className={
                        !address2 && input.name === "employerAddress2" && "mb-3"
                      }
                      key={index}
                    >
                      <>
                        {input.name === "employerAddress2" && !address2 && (
                          <div
                            className="text-start d-flex align-items-center"
                            style={{ height: "100%" }}
                          >
                            <button
                              style={{
                                background: "none",
                                border: "none",
                                textDecoration: "underline",
                              }}
                              onClick={() => setAddress2(true)}
                            >
                              <AiOutlinePlus className="me-2" />
                              Add Address Line 2
                            </button>
                          </div>
                        )}
                        <Form.Group
                          style={
                            input.name === "employerAddress2" && !address2
                              ? { visibility: "hidden", display: "none" }
                              : {}
                          }
                          className="mb-3 text-start"
                          controlId={index}
                        >
                          <Form.Label>{input.label}</Form.Label>
                          <InputGroup>
                            {input.prependIcon && (
                              <InputGroup.Text
                                id="inputGroupPrepend"
                                style={
                                  errors[input.name] &&
                                  touched[input.name] &&
                                  styles.inputGroupTextStyle
                                }
                              >
                                @
                              </InputGroup.Text>
                            )}
                            <Form.Control
                              type={input.name === "startDate" || input.name === "endDate" ? "date" : input.type}
                              placeholder={input.name === "startDate" || input.name === "endDate" ? undefined : input.placeholder}
                              name={input.name}
                              onChange={input.name === "startDate" || input.name === "endDate"
                                ? (e) => setFieldValue(input.name, toStorageFormat(e.target.value))
                                : handleChange}
                              max={input.name === "startDate" || input.name === "endDate" ? getTodayString() : undefined}
                              maxLength={
                                input.name === "employerPhone"
                                  ? 14
                                  : input.name === "employerZip"
                                    ? 5
                                    : 100
                              }
                              value={input.name === "startDate" || input.name === "endDate"
                                ? toDateInputFormat(values[input.name])
                                : values[input.name]}
                              style={
                                errors[input.name] && touched[input.name]
                                  ? styles.errorStyle
                                  : null
                              }
                              disabled={userData.submissionDate}
                              onKeyUp={input.name === "employerPhone" ? (e) => formatService.formatPhone(e) : null}
                            />
                            {errors[input.name] && touched[input.name] && (
                              <InputGroup.Text
                                style={styles.inputGroupTextStyle}
                              >
                                <AiOutlineExclamationCircle
                                  style={styles.errIcon}
                                />
                              </InputGroup.Text>
                            )}
                          </InputGroup>
                          {errors[input.name] && touched[input.name] ? (
                            <div style={styles.fieldErrMessage}>
                              {errors[input.name]}
                            </div>
                          ) : null}
                        </Form.Group>
                        {input.name === "endDate" && (
                          <Form.Check
                            id="currentEmployer"
                            label="This is my Current Employer"
                            type="checkbox"
                            name="currentEmployer"
                            checked={!!values.currentEmployer}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFieldValue("currentEmployer", isChecked);
                              if (isChecked) {
                                let dateToday = new Date();
                                let month = String(dateToday.getMonth() + 1).padStart(2, '0');
                                let day = String(dateToday.getDate()).padStart(2, '0');
                                let dateNow = month + "/" + day + "/" + dateToday.getFullYear();
                                setFieldValue("endDate", dateNow);
                              } else {
                                setFieldValue("endDate", "");
                              }
                            }}
                            disabled={userData.submissionDate}
                          />
                        )}
                      </>
                    </Col>
                  ))}
                </Row>
              </Container>
            </section>

            <Button type="submit" className="m-3" onClick={() => handleAdd(values)}>
              Add
            </Button>
          </Form>
        </Container>
      )}
    </Formik>
  );
};

export default EmployersForm;
